<template>
  <div class="relative">
    <FloatingMenu
      v-if="active"
      class="absolute bottom-18"
      background-color="bg-gray-400"
      style="--arrowLeft: 1.375rem"
      data-testid="annotations-control-menu"
    >
      <div class="flex flex-col mb-1 p-1">
        <div class="flex flex-col justify-center items-center mb-5">
          <button class="menu-control-btn w-7 h-7 my-2" data-testid="annotations-delete" @click="reset">
            <TrashIcon class="w-6 h-6" />
          </button>
          <button class="menu-control-btn w-7 h-7" data-testid="annotations-undo" @click="undo">
            <ArrowUturnLeftIcon class="w-6 h-6" />
          </button>
        </div>
        <DeckAnnotationsColor
          v-for="color in colors"
          :key="color"
          :selected-color="stroke"
          :color="color as AnnotationColors"
          @click="updateStroke"
        />
      </div>
    </FloatingMenu>
    <VTooltip hide-on-breakpoint="sm" :direction="tooltipDirection">
      <template #content>
        <div class="whitespace-nowrap">
          <span>Annotations&nbsp;</span>
          <span class="text-gray-400">({{ modifierKey }} + A)</span>
        </div>
      </template>
      <DeckControlsContainer>
        <DeckControlButton class="p-3" data-testid="annotations-toggle" :is-active="active" @click="toggleMenu">
          <PencilIcon class="w-6 h-6" />
        </DeckControlButton>
      </DeckControlsContainer>
    </VTooltip>
  </div>
</template>

<script lang="ts" setup>
import { PencilIcon } from '@heroicons/vue/24/solid'
import { ArrowUturnLeftIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { AnnotationColors } from '@/lib/annotations/constants'
import type { DeckUser } from '@/entities/deck'
import type { TooltipDirection } from '@/components/VTooltip.vue'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  stroke: {
    type: Number,
    default: AnnotationColors.RED,
  },
  active: {
    type: Boolean,
    required: true,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  tooltipDirection: {
    type: String as PropType<TooltipDirection>,
    default: 'right',
  },
})

const emit = defineEmits(['reset', 'undo', 'update:stroke', 'toggle-active'])

const { $analytics } = useNuxtApp()

const isHost = props.user.deckUserType === 'host'

function toggleMenu() {
  if (props.active) {
    $analytics.track(`annotationsClosed`, { isHost })
  } else {
    $analytics.track(`annotationsOpened`, { isHost })
  }
  emit('toggle-active')
}

let { modifierKey } = useKeyboardShortcut({ opts: { a: toggleMenu } })

function reset() {
  $analytics.track('annotationsEraseSelected', { isHost })
  emit('reset')
}

function undo() {
  $analytics.track('annotationsUndoSelected', { isHost })
  emit('undo')
}

function updateStroke(color: number) {
  $analytics.track('annotationsColorSelected', { isHost })
  emit('update:stroke', color)
}

const colors = Object.values(AnnotationColors).filter(x => typeof x === 'number')!
</script>

<style lang="stylus" scoped>
.menu-control-btn
  @apply appearance-none flex items-center rounded-full text-white p-1
  background: rgba(85, 85, 85, 0.6)

.icon
  @apply h-full
</style>
